import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormikProps } from 'formik'
import * as Yup from 'yup'
import { useSelector, useDispatch } from 'react-redux'
import type { ClassifiedSpecialties } from 'src/rest/types/Classified'

import { actions, selectors } from '../../redux'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { useSagaTakeEvery } from '../../hooks/useSaga'
import { AdsFormAlertModalProps } from '../../components/AdsFormAlertModal'
import Icon, { Icons } from '../../components/Icon'
import { filterGeoAddressByPostalCode } from '../../helpers/AddressHelpers'
import { GeoAddress } from '../../rest/types/GeoAddress'

const useUpdateAlert = ({
  specialties,
}: {
  specialties?: ClassifiedSpecialties
}): AdsFormAlertModalProps => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const formikRef = useRef<FormikProps<any>>(null)
  const isOpen = useSelector(selectors.modals.updateAlertModalIsOpen)
  const alert = useSelector(selectors.user.updatedAlert)
  const user = useSelector(selectors.auth.user)
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    GeoAddress[] | null
  >()

  const [updateResponse, update] = useServiceRequest(
    selectors.user.updateAlert,
    actions.user.updateAlertRequest,
    actions.user.updateAlertReset
  )

  const [createResponse, create] = useServiceRequest(
    selectors.user.createAlert,
    actions.user.createAlertRequest,
    actions.user.createAlertReset
  )

  const [getCategoriesResponse, getCategories] = useServiceRequest(
    selectors.classifieds.getCategories,
    actions.classifieds.getCategoriesRequest,
    actions.classifieds.getCategoriesReset
  )

  const [getSpecialitiesResponse, getSpecialities] = useServiceRequest(
    selectors.classifieds.getSpecialties,
    actions.classifieds.getSpecialtiesRequest,
    actions.classifieds.getSpecialtiesReset
  )

  const [addresses, getAddresses] = useServiceRequest(
    selectors.geo.getAddresses,
    actions.geo.getAddressesRequest,
    actions.geo.getAddressesReset
  )

  useEffect(() => {
    if (addresses.success) {
      const filteredAddresses = addresses.data?.filter((ad) =>
        filterGeoAddressByPostalCode(ad.postcode)
      )
      setSuggestedAddresses(filteredAddresses)
    }
  }, [addresses.data, addresses.success])

  useSagaTakeEvery(
    actions.user.updateAlertSuccess,
    useCallback(() => {
      dispatch(actions.modals.setUpdateAlertModalIsOpen(false))
      dispatch(actions.user.setUpdatedAlert(null))
      formikRef.current?.resetForm()
    }, [dispatch])
  )

  useSagaTakeEvery(
    actions.user.createAlertSuccess,
    useCallback(() => {
      dispatch(actions.modals.setUpdateAlertModalIsOpen(false))
      dispatch(actions.user.setUpdatedAlert(null))
      formikRef.current?.resetForm()
    }, [dispatch])
  )

  useEffect(() => {
    getCategories()

    if (!specialties) {
      getSpecialities({ sort: ['name'] })
    }
  }, [getCategories, getSpecialities, specialties])

  return useMemo(
    () => ({
      title: alert?.id
        ? t('modalModifyAlertTitle')
        : t('modalCreateAlertTitle'),
      isOpen,
      onClose: () => {
        dispatch(actions.classifieds.setCurrentArchivedId(null))
        dispatch(actions.modals.setUpdateAlertModalIsOpen(false))
      },
      form: {
        innerRef: formikRef,
        validationSchema: Yup.object().shape({
          categories: Yup.array(Yup.string()).min(1, t('yup_required')),
          term: Yup.string(),
        }),
        initialValues: {
          categories: alert?.categories?.map((c) => c.id) ?? [],
          localization: alert?.localisation ?? '',
          specialities: alert?.specialities?.map((s) => s.id) ?? [],
          term: alert?.terms ?? '',
        },
        enableReinitialize: true,
        fields: {
          categories: {
            label: t('modalAlertCategoryField'),
            placeholder: t('modalAlertCategoryFieldPlaceholder'),
            options: getCategoriesResponse.data?.map((cat) => ({
              label: cat.name ?? '',
              value: cat.id,
            })),
            isSearchable: false,
            isMulti: true,
            leftAdornment: <Icon icon={Icons.profil} width={24} height={24} />,
          },
          localization: {
            label: t('modalAlertLocalizationField'),
            placeholder: t('modalAlertLocalizationFieldPlaceholder'),
            suggestions: suggestedAddresses?.length
              ? suggestedAddresses?.map((address) => ({
                  label: address.label,
                  value: address.label,
                  params: address,
                }))
              : [],
            onlySuggestion: true,
            loadSuggestions: (value: string) => {
              if (value && value.length > 3) {
                getAddresses({ q: value, type: 'municipality' })
              }
            },
            onSubmit: (selectedValue: any) => {
              if (selectedValue?.params) {
                dispatch(
                  actions.classifieds.setSelectedAddress(selectedValue?.params)
                )
              }
            },
          },
          specialities: {
            label: t('modalAlertSpecialityField'),
            placeholder: t('modalAlertSpecialityFieldPlaceholder'),
            options: (specialties || getSpecialitiesResponse.data)?.map(
              (cat) => ({
                label: cat.name ?? '',
                value: cat.id,
              })
            ),
            isSearchable: false,
            isMulti: true,
            leftAdornment: (
              <Icon icon={Icons.stethoscopeSecondary} width={24} height={24} />
            ),
          },
          term: {
            label: t('modalAlertTermField'),
            placeholder: t('modalAlertTermFieldPlaceholder'),
          },
        },
        submitButton: {
          text: t('modalAlertSubmit'),
          isLoading: updateResponse.pending || createResponse.pending,
        },
        onSubmit: (values) => {
          if (alert?.id) {
            update({
              id: alert?.id,
              terms: values.term,
              localisation: values.localization,
              specialities: values.specialities,
              categories: values.categories,
            })
          } else {
            create({
              userId: user?.id,
              terms: values.term,
              localisation: values.localization,
              specialities: values.specialities,
              categories: values.categories,
            })
          }
        },
      },
    }),
    [
      alert?.id,
      alert?.categories,
      alert?.localisation,
      alert?.specialities,
      alert?.terms,
      t,
      isOpen,
      getCategoriesResponse.data,
      suggestedAddresses,
      specialties,
      getSpecialitiesResponse.data,
      updateResponse.pending,
      createResponse.pending,
      dispatch,
      getAddresses,
      update,
      create,
      user?.id,
    ]
  )
}

export default useUpdateAlert
